import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useNavigation } from 'react-router-dom';

import Button from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Tooltip from '@/components/Tooltip';
import { useUploadPdfButton } from '@/components/UploadPDFButton/hooks';
import { useIsCollaborator, useIsExtendedCollaborator } from '@/hooks/user';
import LogoutModalIcon from '@/icons/logout-modal.svg';
import LogoutIcon from '@/icons/logout.svg';
import logoImage from '@/images/logo.svg';
import routes from '@/routes';
import { logout } from '@/store/profile/thunks';
import { setIsModalContactOpen } from '@/store/site';
import { cn, showError, showSuccess } from '@/utils';

import getSitemap from './sitemap';

function Sidebar() {
	const [open, setOpen] = useState(false);
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const user = useSelector(state => state.profile.user);
	const currentUrl = window.location.pathname;
	const isCollaborator = useIsCollaborator();
	const isExtendedCollaborator = useIsExtendedCollaborator();
	const sitemap = getSitemap(isCollaborator, isExtendedCollaborator);
	const navigation = useNavigation();

	const {
		inputProps,
		loading: uploadPdfLoading,
		onClick: onUploadPdfButtonClick
	} = useUploadPdfButton();

	const handleClickContactButton = () => {
		dispatch(setIsModalContactOpen(true));
	};

	async function handleLogout() {
		try {
			setOpen(false);
			await dispatch(logout()).unwrap();
			localStorage.removeItem('with_gclid');
			showSuccess(t('common.logoutSuccess'));
			navigate(routes.home);
		} catch (error) {
			showError(error);
		}
	}

	const renderDivider = () => (
		<div className="my-3 h-[1px] w-full bg-grey-500" />
	);

	const renderItemContent = item => {
		const isCurrentActive = currentUrl.includes(item.to);
		const isLoadingNextRoute = !!navigation.location?.pathname;
		const isNextActive =
			isLoadingNextRoute && navigation.location.pathname.includes(item.to);
		const isActive = isLoadingNextRoute ? isNextActive : isCurrentActive;

		return (
			<>
				<item.icon
					className={cn(
						'text-grey-300 transition-colors',
						isActive && 'text-red',
						'group-hover:text-white'
					)}
				/>
				<span
					className={cn(
						'text-sm font-semibold leading-6 text-grey-300 transition-colors',
						isActive && 'text-white',
						'group-hover:text-white'
					)}
				>
					{t(item.text)}
				</span>
			</>
		);
	};

	const renderTopSection = () => (
		<Link to={routes.myDocuments} className="mb-8 pl-3">
			<img src={logoImage} alt="Logo" width={80} height={24} />
		</Link>
	);

	const renderBottomSection = () => (
		<div className="grid w-full gap-2 pl-2">
			<Tooltip
				tooltipContentProps={{ className: 'bg-grey-500' }}
				title={user?.email || ''}
			>
				<p className="max-w-[196px] truncate pl-1 text-xs leading-5 text-grey-500">
					{user?.email || ''}
				</p>
			</Tooltip>

			<button
				type="button"
				className="flex w-full max-w-[196px] items-center gap-2 rounded p-1 text-xs font-semibold text-grey-500 transition-colors hover:bg-white/10 hover:text-red"
				onClick={() => setOpen(true)}
			>
				<LogoutIcon />
				<span>{t('common.logout')}</span>
			</button>
		</div>
	);

	return (
		<>
			<div className="w-[252px] shrink-0" />
			<div className="fixed flex h-dvh w-[252px] flex-col rounded-br-xl rounded-tr-xl bg-black px-3 py-7">
				{renderTopSection()}

				<div className="flex h-full flex-col justify-between">
					{sitemap.map(({ links }, index) => (
						<div
							className={`w-228px flex flex-col items-start justify-start ${index ? 'gap-0' : 'gap-2'}`}
							key={`sitemap-${index}`}
						>
							{links.map(item => {
								if (item.to === routes.newDocument)
									return (
										<div className="w-full" key={item.to}>
											<Button
												className="group w-full justify-start gap-3 px-2 py-2"
												onClick={onUploadPdfButtonClick}
												loading={uploadPdfLoading}
											>
												{renderItemContent(item)}
											</Button>
											<input {...inputProps} />
										</div>
									);
								if (item.to === routes.contact)
									return (
										<Button
											className="group w-full justify-start gap-3 px-2 py-2"
											key={item.to}
											onClick={handleClickContactButton}
											type="button"
										>
											{renderItemContent(item)}
										</Button>
									);

								return (
									<Link
										className="group w-full justify-start rounded text-left text-sm leading-7 text-white transition-colors hover:text-red"
										key={item.to}
										to={item.to}
									>
										<div className="flex gap-3 px-2 py-2 hover:bg-white/10">
											{renderItemContent(item)}
										</div>
									</Link>
								);
							})}
						</div>
					))}
				</div>
				{renderDivider()}
				{renderBottomSection()}

				<ConfirmationModal
					open={open}
					onClose={() => setOpen(false)}
					title={t('common.logout')}
					text={t('common.logoutConfirmation')}
					confirmText={t('common.yesLogout')}
					onConfirm={handleLogout}
					Icon={LogoutModalIcon}
					fullIcon
				/>
			</div>
		</>
	);
}

export default Sidebar;
